import React, { ReactElement, RefCallback, RefObject } from 'react';
import camelcase from 'camelcase-es5';

import Icon from '../Icon/Icon';
import Box from '../Box/Box';
import Text from '../Text';
import BadgeBase from '../../components/Badge/BadgeBase';
import Asset from '../../components/Asset';
import useIndeterminate from '../../hooks/useIndeterminate';
import { TAsset, TIcon } from '../../types/TStyleVariables';
import { BadgeSignals, TBadgeSignals } from '../../types/TEBadgeSignal';

export type TSelectionInputBadge = {
  /** The badge label text */
  label: string;
  /** The badge signal. Defaults to `info` */
  signal?: TBadgeSignals;
};

type TSelectionInput = {
  /** value of the checkbox dom input*/
  value: string;
  /** The id of the checkbox input, also used on the label as htmlFor */
  id?: string;
  /** Whether it is checked */
  isChecked: boolean;
  /** The label text. Also passed into aria-label attribute on the input itself*/
  labelText?: string;
  ariaLabel?: string;
  // ** Count of items in a formatted string */
  counter?: string;
  /** Only visible on mobile + stacked */
  icon?: TIcon;
  /** Secondary text below the label. Accepts a string or ReactNode to support inline links. */
  helperText?: React.ReactNode;
  disabled?: boolean;
  /** On component change */
  onChange?: React.FormEventHandler<HTMLInputElement>;
  /** Component name */
  componentName: string;
  CheckMark?: () => ReactElement;
  IndeterminateIcon?: () => ReactElement;
  inputRef?: RefObject<HTMLInputElement> | RefCallback<HTMLInputElement>;
  name?: string;
  /**
   * Renders the mixed (partially selected) state. Mirrors the native `indeterminate` DOM
   * property, so it is independent of `isChecked` and takes visual precedence over it.
   */
  isIndeterminate?: boolean;
  tabIndex?: number;
  badge?: TSelectionInputBadge;
  assetName?: TAsset;
  assetSize?: 'm' | 'l';
  /** Optional price text to display */
  price?: string;
  /** Optional slashed (original) price to display alongside price */
  slashedPrice?: string;
  /** Optional children to render below the main content */
  children?: React.ReactNode;
} & React.HTMLAttributes<any>;

type TSelectionInputBody = {
  icon?: TIcon;
  labelText?: string;
  counter?: string;
  helperText?: React.ReactNode;
  badge?: TSelectionInputBadge;
  assetName?: TAsset;
  assetSize?: 'm' | 'l';
  price?: string;
  slashedPrice?: string;
  children?: React.ReactNode;
};

type TInput = {
  ariaLabel?: string;
  isChecked: boolean;
  isIndeterminate?: boolean;
  id: string;
  onChange?: React.FormEventHandler<HTMLInputElement>;
  componentName: string;
  value: string;
  inputRef?: RefCallback<HTMLInputElement>;
  disabled?: boolean;
  htmlNameAttr?: string;
  tabIndex?: number;
} & React.InputHTMLAttributes<any>;

const Input = ({
  value,
  isChecked,
  isIndeterminate,
  onChange,
  ariaLabel,
  id,
  componentName,
  inputRef,
  disabled,
  htmlNameAttr,
  tabIndex,
}: TInput) => (
  <Box
    parent="SelectionInput"
    name="Input"
    as="input"
    type={componentName.toLowerCase()}
    id={id}
    ref={inputRef}
    htmlNameAttr={htmlNameAttr ? htmlNameAttr : id}
    checked={isChecked}
    // `indeterminate` is set on the node by `useIndeterminate`; the state class mirrors it so
    // the styling is already correct on the server and on the first paint.
    states={isIndeterminate ? 'indeterminate' : []}
    value={value}
    onChange={onChange}
    aria-label={ariaLabel}
    disabled={disabled}
    tabIndex={tabIndex}
  />
);

const SelectionInputBody = ({
  labelText,
  counter,
  helperText,
  icon,
  badge,
  assetName,
  assetSize = 'l',
  price,
  slashedPrice,
  children,
}: TSelectionInputBody): ReactElement | null => {
  // If there's no content to display, don't render the body at all
  if (
    !labelText &&
    !counter &&
    !helperText &&
    !icon &&
    !badge?.label &&
    !assetName &&
    !price &&
    !slashedPrice &&
    !children
  ) {
    return null;
  }

  return (
    <Box as="div" parent="SelectionInput" name="Container">
      {icon && (
        <Box as="div" parent="SelectionInput" name="Icon">
          <Icon name={icon} color="text-primary" />
        </Box>
      )}
      {assetName && (
        <Box as="div" parent="SelectionInput" name="Asset">
          <Asset.Square name={assetName} size={assetSize} />
        </Box>
      )}
      <Box as="div" parent="SelectionInput" name="Body">
        <Box as="div" parent="SelectionInput" name="Label">
          {labelText && (
            <Box as="span" parent="SelectionInput" name="LabelText">
              <Text.BodyBase color="text-primary">{labelText}</Text.BodyBase>
            </Box>
          )}
          {counter && (
            <Box as="span" parent="SelectionInput" name="Counter">
              <Text.BodySmall color="text-secondary">{counter}</Text.BodySmall>
            </Box>
          )}
          {(price || slashedPrice) && (
            <Box as="span" parent="SelectionInput" name="Prices">
              {slashedPrice && (
                <Box as="span" parent="SelectionInput" name="SlashedPrice">
                  <Text.BodyBase color="text-secondary">{slashedPrice}</Text.BodyBase>
                </Box>
              )}
              {price && (
                <Box as="span" parent="SelectionInput" name="Price">
                  <Text.BodyBase color="text-primary">{price}</Text.BodyBase>
                </Box>
              )}
            </Box>
          )}
          {Boolean(badge?.label) && (
            <BadgeBase signal={badge?.signal ?? BadgeSignals.Info} utilities={['marginLeftXxxs']}>
              {badge?.label}
            </BadgeBase>
          )}
        </Box>
        {helperText && (
          <Box as="span" parent="SelectionInput" name="Helper" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <Text.BodySmall color="text-secondary">{helperText}</Text.BodySmall>
          </Box>
        )}
        {children && (
          <Box as="div" parent="SelectionInput" name="Children">
            {children}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const SelectionInput = ({
  value,
  isChecked,
  isIndeterminate = false,
  labelText,
  ariaLabel,
  counter,
  icon,
  helperText,
  onChange,
  componentName,
  CheckMark,
  IndeterminateIcon,
  inputRef,
  disabled,
  id = `${componentName}-${labelText ? `${camelcase(labelText)}-` : ''}${camelcase(value)}`,
  name,
  tabIndex,
  badge,
  assetName,
  assetSize,
  price,
  slashedPrice,
  children,
  ...rest
}: TSelectionInput): ReactElement => {
  const { setInputRef, applyIndeterminate } = useIndeterminate(isIndeterminate, inputRef);

  const handleChange: React.FormEventHandler<HTMLInputElement> = (event) => {
    // The click already cleared `indeterminate` on the node. Put the prop value back before
    // handing over, so the state does not drift when the consumer keeps it unchanged.
    applyIndeterminate();
    onChange?.(event);
  };

  return (
    <Box as="label" name="SelectionInput" modifiers={componentName} htmlFor={id} {...rest}>
      <Box as="div" parent="SelectionInput" name="InputContainer">
        <Input
          value={value}
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
          id={id}
          onChange={handleChange}
          ariaLabel={ariaLabel}
          componentName={componentName}
          inputRef={setInputRef}
          disabled={disabled}
          htmlNameAttr={name}
          tabIndex={tabIndex}
        />
        {/* Mirrors the native rendering: the mixed state wins over the checked state. */}
        {isIndeterminate && IndeterminateIcon && <IndeterminateIcon />}
        {!isIndeterminate && isChecked && CheckMark && <CheckMark />}
      </Box>
      <SelectionInputBody
        icon={icon}
        counter={counter}
        labelText={labelText}
        helperText={helperText}
        badge={badge}
        assetName={assetName}
        assetSize={assetSize}
        price={price}
        slashedPrice={slashedPrice}
        children={children}
      />
    </Box>
  );
};

export default SelectionInput;
